import { ReplicantABTests } from '../../common/ReplicantABTests';
import { ReplicantError } from '../../Errors';
import { teaRandom } from '../../math/prng/prng';
import { Replicant } from '../../ReplicantConfig';
import { ABTestBucket } from '../../ReplicantRuleset';
import { CreateServerReplicantActionAPIOpts } from '../../server/ServerReplicant';
import { generateRandomId } from '../../utils/Utils';
import { PurchaseInfo, ReplicantAsyncActionAPI } from '../ReplicantAPI';
import { generateAsyncGettersAPI } from './AsyncGettersAPIImpl';
import { createReportingAPIs } from './createReportingAPIs';
import { createStubTelegramAPI } from './createTelegramAPI';
import { generateFlushMessages, generateMessagesAPI } from './MessagesAPIImpl';
import { createSharedStatesAPI } from './SharedStatesAPIImpl';
import { ReplicantBrowserFetcher } from './utils/impl/ReplicantBrowserFetcher';
import { getPurchaseHistory } from './utils/PurchaseHistory';

/** API implementation where the browser acts as a server. Used by offline mode clients. */
export function createBrowserReplicantActionAPI<T extends Replicant>(opts: CreateServerReplicantActionAPIOpts<T>) {
    const { apiAccess, id, entry, delayedActions, invokeTime, async, messages, consistentFetchIds, userAssetsBaseUrl } =
        opts;

    const abTestsApi = new ReplicantABTests({
        abTests: apiAccess.config.ruleset?.abTests ?? {},
        dynamicConfig: opts.abTestsDynamicConfig,
    });
    const flushMessages = generateFlushMessages({ consistentFetchIds, delayedActions, apiAccess });
    const dateNow = () => invokeTime;
    const getClockOffset = () => entry.metainfo.clockOffset || 0;
    const messagesAPI = generateMessagesAPI({
        senderId: id,
        dateNow,
        delayedActions,
        getClockOffset,
        async: !!async,
        messages,
    });
    const reportingAPIs = createReportingAPIs({ apiAccess, delayedActions, id, sessionId: opts.sessionId ?? '' });

    const random = () => (async ? Math.random() : teaRandom(id, entry.metainfo.random));

    const sharedStates = createSharedStatesAPI({
        apiAccess,
        async,
        consistentFetchIds,
        dateNow,
        delayedActions,
        entry: opts.entry,
        getClockOffset,
        random,
        sharedStates: opts.sharedStates,
        userId: id,
    });

    const fetcher = opts.fetcher ?? new ReplicantBrowserFetcher();

    const api: ReplicantAsyncActionAPI<T> = {
        isAsync: !!async,

        asyncGetters: generateAsyncGettersAPI({
            apiAccess,
            consistentFetchIds,
            entry,
            id,
            invokeTime,
            sessionId: opts.sessionId ?? '',
            ...reportingAPIs,
        }),

        math: {
            random: () => (async ? Math.random() : teaRandom(id, entry.metainfo.random)),
        },

        getUserID: () => id,
        getSessionID: () => opts.sessionId ?? '',

        date: {
            now: () => invokeTime + (entry.metainfo.clockOffset || 0),
        },

        searchPlayers: async (query) => {
            return apiAccess.searchPlayers(query);
        },

        countPlayers: async (query) => {
            return apiAccess.countPlayers(query);
        },

        getMentionCountForPagePost: async () => {
            throw new Error('Cannot get mention count in browser replicant');
        },

        fetch: (...args) => fetcher.fetch(...args),

        fetchStates: async (ids) => {
            // Note: async API call results are reported back to the client, so the client can replay the action.
            // If we have an API results that is close to a meg, we can't really split it; hence, just return an
            // error here. We shouldn't be doing that anyway.
            const results = await apiAccess.fetchStates(ids, { consistentFetchIds });
            if (results.unprocessedIds && results.unprocessedIds.length > 0) {
                throw new Error('Requesting too many profiles in API');
            }
            return results.states;
        },

        generateOrGetNativeBridgeSecret: () => {
            if (entry.chatbotMetainfo?.nativeBridgeSecret) {
                return entry.chatbotMetainfo.nativeBridgeSecret;
            }

            const bridgeSecret = generateRandomId(random);

            delayedActions.push({
                type: 'action',
                action: () => apiAccess.setNativeBridgeSecret(id, bridgeSecret),
                apiMethodName: 'api.generateOrGetNativeBridgeSecret',
            });

            return bridgeSecret;
        },

        internalKvStore: {
            get: (...args) => opts.apiAccess.internalKvStore.get(...args),
            getBatch: (...args) => opts.apiAccess.internalKvStore.getBatch(...args),
        },

        kvStore: {
            get: (...args) => opts.apiAccess.kvStore.get(...args),
            getBatch: (...args) => opts.apiAccess.kvStore.getBatch(...args),
            send: (...args) => opts.apiAccess.kvStore.send(...args),
            sendBatch: (...args) => opts.apiAccess.kvStore.sendBatch(...args),
        },

        loginLinks: {
            createLoginToken() {
                throw Error('Login links are disabled in offline mode');
            },
            createWebPlayerToken() {
                throw Error('Login links are disabled in offline mode');
            },
        },

        abTests: {
            getBucketID: (testId) =>
                abTestsApi.getBucketID(entry.metainfo, testId) as
                    | ABTestBucket<NonNullable<T['ruleset']['abTests']>, typeof testId>
                    | undefined,

            assign: (testId, bucketId) => abTestsApi.assign(entry.metainfo, id, testId, bucketId as string | undefined),
            unassign: (testId) => abTestsApi.unassign(entry.metainfo, id, testId),
        },

        otp: {
            verifyOtp: () => Promise.reject('OTP is disabled in offline mode'),
        },

        postMessage: messagesAPI,
        flushMessages,
        scheduledActions: opts.scheduledActionsApi,

        telegram: createStubTelegramAPI('Telegram API is disabled in offline mode'),

        payments: {
            createXsollaPaymentToken: () => {
                throw new Error('createXsollaPaymentToken is not implemented');
            },
            createCheckoutSession() {
                throw Error('Payments are disabled in offline mode');
            },
            createInvoice() {
                throw Error('Payments are disabled in offline mode');
            },
            createKomojuSession() {
                throw Error('Payments are disabled in offline mode');
            },
            createKomojuPayment() {
                throw Error('Payments are disabled in offline mode');
            },
            createPaymentIntent() {
                throw Error('Payments are disabled in offline mode');
            },
        },

        paymentSubscriptions: {
            getStatus: () => {
                // undefined here is effectively no subscription has ever been initialized, but is a valid state
                return Promise.resolve(undefined);
            },
            initiateWithOTPVerification() {
                throw new Error('not implemented');
            },
            verifyOTP() {
                throw new Error('not implemented');
            },
            initiate: (_) => {
                throw new Error('not implemented');
            },
            verify: () => {
                throw new Error('not implemented');
            },
            cancel: () => {
                throw new Error('not implemented');
            },
        },

        purchases: {
            getPurchaseHistory: () => getPurchaseHistory(entry.metainfo, entry.chatbotMetainfo ?? {}),
            validatePurchase: async (purchaseOpts) => {
                // In dev mode platform mock will decode purchase data to json string(signedRequest)
                // It will allow us to restore purchase details on server side and simulate purchase processing

                /* NOTE: this fails in IOS trying to JSON parse receiptData which is just a JWT string
                const data = JSON.parse(
                    purchaseOpts.paymentProcessor === 'ios' ? purchaseOpts.receiptData : purchaseOpts.signedRequest,
                );
                */

                // Successfully verify payment in an offline mode
                if (purchaseOpts.paymentProcessor === 'ios') {
                    if (!purchaseOpts.productId) {
                        throw new ReplicantError(
                            'Property purchaseOpts.productId is required in offline Replicant',
                            'replication_error',
                            'payment_signature_verification_error',
                        );
                    }

                    return {
                        productId: purchaseOpts.productId,
                        purchaseToken: purchaseOpts.signedRequest,
                        developerPayload: {},
                    };
                }

                const data = JSON.parse(purchaseOpts.signedRequest);

                if (!data)
                    throw new ReplicantError(
                        'Wrong signedRequest',
                        'replication_error',
                        'payment_signature_verification_error',
                    );
                data.purchase_token = data.purchaseToken;

                // Check if already recorded in the transaction history.
                if (
                    entry.metainfo.purchaseHistory &&
                    entry.metainfo.purchaseHistory.some((x) => x.purchase_token === data.purchase_token)
                ) {
                    throw new ReplicantError(
                        'Purchase has already been logged before.',
                        'replication_error',
                        'payment_already_purchased_error',
                    );
                }

                // Convert mock payment object to PurchaseInfo
                const record: PurchaseInfo = {
                    algorithm: 'HMAC-SHA256',
                    developer_payload: data.developerPayload,
                    is_consumed: false,
                    issued_at: data.purchaseTime,
                    payment_id: 'awsome_payment_id',
                    product_id: data.productID,
                    purchase_time: data.purchaseTime,
                    purchase_token: data.purchaseToken,
                };

                // Apply clock offset to purchase timestamps:
                const isMillisecondTimestamp = record.issued_at.toString().length === 13;
                const offset = isMillisecondTimestamp ? getClockOffset() : Math.round(getClockOffset() / 1000);
                record.issued_at = Number(record.issued_at) + offset;
                record.purchase_time = Number(record.purchase_time) + offset;

                // All good, add it to the purchaseHistory to be recorded.
                entry.metainfo.purchaseHistory = entry.metainfo.purchaseHistory ?? [];
                entry.metainfo.purchaseHistory.push(record);

                return {
                    productId: data.productID,
                    purchaseToken: data.purchaseToken,
                    developerPayload: data.developerPayload,
                };
            },

            consumePurchase: () => {
                throw Error('Not implemented in offline mode');
            },
        },

        sharedStates,

        ...reportingAPIs,

        chatbot: opts.chatbotApi,

        nukeUserMetainfo: () => {
            entry.metainfo = { random: { n: 0 } };
            entry.chatbotMetainfo = undefined;
            entry.metainfoMVCC = {};
        },

        setClockOffset: (millis: number) => {
            entry.metainfo.clockOffset = Math.floor(millis);
        },

        getClockOffset,

        getPushNotificationLastTargetedAt: () =>
            Math.max(
                entry.chatbotMetainfo?.channelLastTargetedAt?.android ?? 0,
                entry.chatbotMetainfo?.channelLastTargetedAt?.ios ?? 0,
            ),

        getUserAssetUrl: (assetId: string) => userAssetsBaseUrl + assetId,
    };

    return api;
}
